import React, { useMemo } from "react";
import { pageGutterPx } from "../../core/breakpoints";
import { C } from "../../core/constants";
import { APP_FONT_SIZE } from "../../core/theme";
import { useIsMobile } from "../../hooks/useMediaQuery";
import Button from "../../ui/Button";
import GlassCard from "../../ui/GlassCard";
import ReadingReplayListItem from "../predictions/components/ReadingReplayListItem";
import { groupReadingsByDate, READING_HISTORY_FEATURE_FILTERS } from "../predictions/readingReplay";
import useReadingHistory from "./useReadingHistory";

export default function ReadingHistoryPage({ acc, setPage, t, locale, formatInTimezone }) {
  const fs = APP_FONT_SIZE;
  const isMobile = useIsMobile();
  const gutter = pageGutterPx(isMobile);
  const { loading, loadingMore, error, data, feature, setFeatureFilter, loadMore } = useReadingHistory();
  const items = data?.items || [];
  const hasData = Boolean(data);
  const hasMore = Boolean(data?.pagination?.has_more);
  const totalCount = data?.pagination?.total_count;

  const groupedItems = useMemo(
    () => groupReadingsByDate(items, { locale, t }),
    [items, locale, t]
  );

  return (
    <div style={{ padding: gutter, maxWidth: 760, margin: "0 auto" }}>
      <button
        type="button"
        onClick={() => setPage("dashboard")}
        style={{
          border: `1px solid ${acc.primary}44`,
          borderRadius: 999,
          background: `${acc.primary}12`,
          color: acc.primary,
          fontSize: 12,
          cursor: "pointer",
          padding: "6px 14px",
          marginBottom: 16,
        }}
      >
        ← {t("reading_history_back_dashboard")}
      </button>

      <GlassCard style={{ padding: isMobile ? "22px 18px" : "26px 24px", marginBottom: 20 }} accent={acc.primary} glow>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: 12, flexWrap: "wrap" }}>
          <div>
            <div style={{ fontSize: 11, letterSpacing: "0.14em", color: acc.secondary, marginBottom: 8, textTransform: "uppercase" }}>
              {t("reading_history_eyebrow")}
            </div>
            <h1 style={{ fontFamily: "'Cormorant Garamond',serif", fontSize: "clamp(30px,3.5vw,42px)", fontWeight: 400, color: C.textPrimary, margin: 0 }}>
              {t("reading_history_title")}
            </h1>
            <p style={{ color: C.textSecondary, fontSize: fs - 1, marginTop: 8, marginBottom: 0, lineHeight: 1.5 }}>
              {t("reading_history_subtitle")}
            </p>
          </div>
          {hasData && Number.isFinite(totalCount) && (
            <div
              style={{
                padding: "8px 14px",
                borderRadius: 999,
                border: `1px solid ${acc.tertiary}44`,
                background: `${acc.tertiary}14`,
                color: C.textPrimary,
                fontSize: 13,
                whiteSpace: "nowrap",
              }}
            >
              {t("reading_history_count").replace("%{count}", String(totalCount))}
            </div>
          )}
        </div>
      </GlassCard>

      <div
        style={{
          display: "flex",
          gap: 8,
          overflowX: "auto",
          paddingBottom: 6,
          marginBottom: 18,
          WebkitOverflowScrolling: "touch",
        }}
      >
        {READING_HISTORY_FEATURE_FILTERS.map((filter) => {
          const active = feature === filter.id;
          return (
            <button
              key={filter.id ?? "all"}
              type="button"
              onClick={() => setFeatureFilter(filter.id)}
              disabled={loading && hasData}
              style={{
                flex: "0 0 auto",
                display: "inline-flex",
                alignItems: "center",
                gap: 6,
                border: `1px solid ${active ? `${acc.primary}88` : "rgba(255,255,255,0.12)"}`,
                borderRadius: 999,
                padding: "8px 14px",
                background: active ? `linear-gradient(135deg, ${acc.primary}28, ${acc.tertiary}18)` : "rgba(255,255,255,0.03)",
                color: active ? C.textPrimary : C.textSecondary,
                fontSize: 12,
                cursor: loading && hasData ? "not-allowed" : "pointer",
                whiteSpace: "nowrap",
                boxShadow: active ? `0 0 16px ${acc.primary}22` : "none",
              }}
            >
              <span aria-hidden style={{ fontSize: 14, opacity: active ? 1 : 0.75 }}>{filter.icon}</span>
              {t(filter.labelKey)}
            </button>
          );
        })}
      </div>

      {loading && !hasData && (
        <GlassCard style={{ padding: 24, textAlign: "center" }} accent={acc.tertiary}>
          <div style={{ fontSize: 28, marginBottom: 8, opacity: 0.8 }}>☾</div>
          <div style={{ color: C.textSecondary, fontSize: fs - 1 }}>{t("reading_history_loading")}</div>
        </GlassCard>
      )}

      {!loading && !hasData && error && (
        <GlassCard style={{ padding: 24 }} accent={acc.secondary}>
          <div style={{ color: C.textSecondary, fontSize: fs - 1 }}>{t(error)}</div>
        </GlassCard>
      )}

      {hasData && items.length === 0 && (
        <GlassCard style={{ padding: 28, textAlign: "center" }} accent={acc.tertiary}>
          <div style={{ fontSize: 32, marginBottom: 10, color: acc.primary }}>◎</div>
          <div style={{ color: C.textPrimary, fontSize: fs, marginBottom: 6 }}>{t("reading_history_empty")}</div>
          <div style={{ color: C.textMuted, fontSize: 13, marginBottom: 16 }}>{t("reading_history_empty_hint")}</div>
          <Button variant="outline" acc={acc} onClick={() => setPage("dashboard")} style={{ padding: "8px 16px", fontSize: 13 }}>
            {t("reading_history_empty_cta")}
          </Button>
        </GlassCard>
      )}

      {hasData && items.length > 0 && (
        <>
          {groupedItems.map((group) => (
            <div key={group.key} style={{ marginBottom: 20 }}>
              <div
                style={{
                  fontSize: 11,
                  letterSpacing: "0.12em",
                  textTransform: "uppercase",
                  color: C.textMuted,
                  marginBottom: 10,
                  paddingLeft: 4,
                }}
              >
                {group.label}
              </div>
              {group.items.map((item, idx) => (
                <ReadingReplayListItem
                  key={item.prediction_id || `${group.key}-${idx}`}
                  item={item}
                  isLast={idx === group.items.length - 1}
                  acc={acc}
                  t={t}
                  locale={locale}
                  formatInTimezone={formatInTimezone}
                  setPage={setPage}
                  screenClass="readingHistory"
                  ctaIdPrefix="reading_history_replay"
                  variant="history"
                  ctaLabelKey="reading_history_replay_cta"
                />
              ))}
            </div>
          ))}
          {hasMore && (
            <div style={{ display: "flex", justifyContent: "center", marginTop: 8 }}>
              <Button
                variant="outline"
                acc={acc}
                onClick={loadMore}
                style={{
                  padding: "10px 22px",
                  fontSize: 13,
                  opacity: loadingMore ? 0.7 : 1,
                  cursor: loadingMore ? "not-allowed" : "pointer",
                }}
              >
                {loadingMore ? t("reading_history_loading") : t("reading_history_load_more")}
              </Button>
            </div>
          )}
        </>
      )}
    </div>
  );
}
